// EP-135 Tier 1 — End-term modal. Admin+ only. Records ended_on +
// ended_reason (D-11a: governance act, not a callback).
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Modal } from '@/components/ui/Modal'
import { Button, Input, Textarea } from '@/components/ui'
import { useNotificationStore } from '@/stores'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { CommitteeMemberSummary } from '@/types/governance'

export interface EndTermModalProps {
  isOpen: boolean
  member: CommitteeMemberSummary | null
  onClose: () => void
  onConfirm: (reason: string, ended_on?: string) => Promise<void>
  loading?: boolean
}

export function EndTermModal({ isOpen, member, onClose, onConfirm, loading }: EndTermModalProps) {
  const { t } = useTranslation('governance')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const translateError = useApiErrorTranslator('governance')

  const [reason, setReason] = useState('')
  const [endedOn, setEndedOn] = useState('')

  const handleConfirm = async () => {
    if (!reason.trim()) {
      addToast({ type: 'error', message: t('committee.messages.reason_required') })
      return
    }
    try {
      await onConfirm(reason.trim(), endedOn || undefined)
      setReason('')
      setEndedOn('')
    } catch (err) {
      addToast({ type: 'error', message: translateError(err) })
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('committee.messages.end_term_title')}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={!reason.trim()}
            loading={loading}
            data-testid="btn-confirm-end-term"
          >
            {t('committee.actions.end_term')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm">
          {member && t('committee.messages.end_term_confirm', { name: member.customer_name })}
        </p>
        <Input
          label={t('committee.messages.end_date_label')}
          type="date"
          value={endedOn}
          onChange={(e) => setEndedOn(e.target.value)}
        />
        <Textarea
          label={t('committee.messages.end_reason_label')}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          required
          rows={3}
          data-testid="input-end-reason"
        />
      </div>
    </Modal>
  )
}
